import { can, currentRole } from '../core/access';
import { appState } from '../core/state';
import { esc, openModal, closeModal, onModalClose, showModalErr } from '../ui/modal';
import { renderFarms } from './farms-view';
import { navigate } from '../core/router';
import { fetchTeams } from '../users/teams-shared';
import { farmInstallCmd, farmCleanupCmd, farmCreateUpdaterCmd, farmDockerRun } from './farm-docker-config';
import { renderLaunchSetupBody } from './launch-tab';
import { downloadMd } from './farm-setup-shared';
import { FARM_LOCATION_OPTIONS } from '../core/farm-locations';
import { FarmKind, normalizeFarmKind, isHostlessFarm } from '../core/farm-kind';
import { duoSetupStepsHtml, wireDuoSetup } from './duo-shared';
import { aggregatorSetupStepsHtml } from './aggregator-shared';
import { t } from '../ui/lang';
export async function showAddFarmModal() {
    // The group <select> is editable only for a superadmin. For an admin the new farm
    // inherits their own group server-side, so the team is shown as read-only text
    // (the admin's own team name) and never submitted.
    const isSuperadmin = currentRole() === 'superadmin';
    let teams = [];
    if (isSuperadmin) {
        try {
            teams = await fetchTeams();
        }
        catch { /* select just shows "(no team)" */ }
    }
    const groupControl = isSuperadmin
        ? `<div class="modal__field">
                <label>${t('common.team')}</label>
                <select id="ff-team">
                    <option value="">${t('farms.no_team')}</option>
                    ${teams.map((team) => `<option value="${team.id}">${esc(team.name)}</option>`).join('')}
                </select>
            </div>`
        : `<div class="modal__field">
                <label>${t('common.team')}</label>
                <p class="modal__text uf-team-readonly">${esc(appState.currentUserTeamName ?? t('farms.no_team'))}</p>
            </div>`;
    const locationOptions = FARM_LOCATION_OPTIONS
        .map((l) => `<option value="${esc(l.code)}">${l.flag ? `${l.flag} ` : ''}${esc(l.label)}</option>`)
        .join('');
    const modal = openModal(`
        <h2 class="modal__title">${t('farms.add_farm_title')}</h2>
        <form id="farm-form" class="modal__form" novalidate>
            <div class="modal__field">
                <label>${t('farms.farm_name')}</label>
                <input id="ff-name" type="text" autocomplete="off" maxlength="100" placeholder="${t('farms.farm_name_ph')}" required />
            </div>
            <div class="modal__field">
                <label>${t('farms.farm_type')}</label>
                <select id="ff-kind">
                    <option value="host">${t('farms.kind_host')}</option>
                    <option value="aggregator">${t('farms.kind_aggregator')}</option>
                    <option value="duo">${t('farms.kind_duo')}</option>
                    <option value="geelark">${t('farms.kind_geelark')}</option>
                    <option value="adspower">${t('farms.kind_adspower')}</option>
                    <option value="mercado_api">${t('farms.kind_mercado_api')}</option>
                </select>
                <p class="modal__hint">${t('farms.kind_hint')}</p>
            </div>
            <div class="modal__field" id="ff-duo-only" style="display:none">
                <label>${t('farms.duo_api_key')}</label>
                <input id="ff-duo-key" type="password" autocomplete="off" placeholder="${t('farms.duo_key_ph')}" />
                <p class="modal__hint">${t('farms.duo_key_hint')}</p>
            </div>
            <div class="modal__field" id="ff-geelark-only" style="display:none">
                <label>${t('farms.geelark_app_id')}</label>
                <input id="ff-geelark-appid" type="text" autocomplete="off" placeholder="${t('farms.geelark_console_ph')}" />
                <label style="margin-top:10px">${t('farms.geelark_api_key')}</label>
                <input id="ff-geelark-key" type="password" autocomplete="off" placeholder="${t('farms.geelark_console_ph')}" />
                <p class="modal__hint">${t('farms.geelark_key_hint')}</p>
            </div>
            <div class="modal__field" id="ff-adspower-only" style="display:none">
                <label>${t('farms.adspower_api_url')}</label>
                <input id="ff-adspower-url" type="text" autocomplete="off" value="http://local.adspower.net:50325" placeholder="http://local.adspower.net:50325" />
                <label style="margin-top:10px">${t('farms.adspower_api_key')}</label>
                <input id="ff-adspower-key" type="password" autocomplete="off" placeholder="${t('farms.adspower_api_ph')}" />
                <p class="modal__hint">${t('farms.adspower_api_hint')}</p>
            </div>
            ${groupControl}
            <div class="modal__field">
                <label>${t('farms.location')}</label>
                <select id="ff-location">${locationOptions}</select>
                <p class="modal__hint">${t('farms.location_hint')}</p>
            </div>
            <div class="modal__field" id="ff-host-only">
                <label class="settings-toggle">
                    <input id="ff-screen-off" type="checkbox" />
                    <span>${t('farms.screen_off_label')}</span>
                </label>
                <p class="modal__hint">${t('farms.screen_off_hint')}</p>
            </div>
            <div id="ff-error" class="modal__error" style="display:none"></div>
            <div class="modal__actions">
                <button type="button" class="modal__cancel" id="ff-cancel">${t('common.cancel')}</button>
                <button type="submit" class="modal__submit">${t('common.create')}</button>
            </div>
        </form>
    `);
    modal.querySelector('#ff-cancel').addEventListener('click', closeModal);
    const kindSel = modal.querySelector('#ff-kind');
    const hostOnly = modal.querySelector('#ff-host-only');
    const duoOnly = modal.querySelector('#ff-duo-only');
    const geelarkOnly = modal.querySelector('#ff-geelark-only');
    const adspowerOnly = modal.querySelector('#ff-adspower-only');
    // Screen-off is a scrcpy-connect behaviour — irrelevant to an agent-less farm
    // (aggregator/duo/geelark/adspower), whose "devices" are remote phones or browser
    // profiles, not scrcpy targets. The provider cred fields show only for their own kind.
    kindSel.addEventListener('change', () => {
        const kind = normalizeFarmKind(kindSel.value);
        hostOnly.style.display = isHostlessFarm(kind) ? 'none' : '';
        duoOnly.style.display = kind === FarmKind.Duo ? '' : 'none';
        geelarkOnly.style.display = kind === FarmKind.Geelark ? '' : 'none';
        adspowerOnly.style.display = kind === FarmKind.Adspower ? '' : 'none';
    });
    modal.querySelector('#farm-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const name = modal.querySelector('#ff-name').value.trim();
        const kind = normalizeFarmKind(kindSel.value);
        const teamSel = modal.querySelector('#ff-team');
        const locSel = modal.querySelector('#ff-location');
        const screenOff = modal.querySelector('#ff-screen-off').checked;
        const duoKey = modal.querySelector('#ff-duo-key').value.trim();
        const geelarkAppId = modal.querySelector('#ff-geelark-appid').value.trim();
        const geelarkKey = modal.querySelector('#ff-geelark-key').value.trim();
        const adspowerUrl = modal.querySelector('#ff-adspower-url').value.trim();
        const adspowerKey = modal.querySelector('#ff-adspower-key').value.trim();
        const errorEl = modal.querySelector('#ff-error');
        const btn = modal.querySelector('.modal__submit');
        errorEl.style.display = 'none';
        if (!name) {
            showModalErr(errorEl, t('farms.err_name_required'));
            return;
        }
        if (name.length > 100) {
            showModalErr(errorEl, t('farms.err_name_too_long'));
            return;
        }
        if (kind === FarmKind.Duo && !duoKey) {
            showModalErr(errorEl, t('farms.err_duo_key_required'));
            return;
        }
        if (kind === FarmKind.Geelark && (!geelarkAppId || !geelarkKey)) {
            showModalErr(errorEl, t('farms.err_geelark_required'));
            return;
        }
        if (kind === FarmKind.Adspower && (!adspowerUrl || !adspowerKey)) {
            showModalErr(errorEl, t('farms.err_adspower_required'));
            return;
        }
        btn.disabled = true;
        try {
            // team_id only sent by a superadmin; admins inherit their group server-side.
            const createBody = {
                name,
                farm_kind: kind,
                location: locSel.value || null,
                screen_off_on_connect: screenOff,
            };
            if (kind === FarmKind.Duo)
                createBody['duo_api_key'] = duoKey;
            if (kind === FarmKind.Geelark) {
                createBody['geelark_app_id'] = geelarkAppId;
                createBody['geelark_api_key'] = geelarkKey;
            }
            if (kind === FarmKind.Adspower) {
                createBody['adspower_api_url'] = adspowerUrl;
                createBody['adspower_api_key'] = adspowerKey;
            }
            if (isSuperadmin && teamSel)
                createBody['team_id'] = teamSel.value ? Number.parseInt(teamSel.value, 10) : null;
            const res = await fetch('/api/farms', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(createBody) });
            const data = await res.json();
            if (!res.ok) {
                showModalErr(errorEl, data.error ?? t('farms.err_create_failed'));
                return;
            }
            closeModal();
            if (kind === FarmKind.Duo) {
                // Duo farms have no agent to launch — just point the admin at the
                // Duo Setup tab (API key + ADB IP whitelist).
                showDuoConfigModal(data.name ?? name);
            }
            else if (kind === FarmKind.Geelark) {
                // GeeLark farms likewise have no agent — API sync + glogin adb.
                showGeelarkConfigModal(data.name ?? name);
            }
            else if (kind === FarmKind.Adspower) {
                // AdsPower farms run a docker agent authenticated by the farm key — show it
                // with the setup pointer, like aggregator.
                if (data.key && data.name)
                    showAdspowerConfigModal(data.name, data.key);
                else
                    void renderFarms();
            }
            else if (kind === FarmKind.MercadoApi) {
                // Nothing to launch: the Setup tab explains how to get the API key, General
                // takes the platform server — open the farm settings straight away.
                if (data.id != null)
                    navigate({ page: 'farms', farmId: data.id, settings: true });
                else
                    void renderFarms();
            }
            else if (data.key && data.name) {
                // Host farms get the docker launch steps; aggregator farms get the
                // host + key to paste into the mobile farm client instead.
                if (kind === FarmKind.Aggregator)
                    showAggregatorConfigModal(data.name, data.key);
                else
                    showLaunchConfigModal(data.name, data.key);
            }
            else
                void renderFarms();
        }
        catch {
            showModalErr(errorEl, t('farms.err_network'));
        }
        finally {
            btn.disabled = false;
        }
    });
}
/** Build the install/cleanup/docker-run commands and a markdown doc for a farm. */
export function buildLaunchSetup(name, key) {
    const relayHost = window.location.origin;
    const installCmd = farmInstallCmd();
    const cleanupCmd = farmCleanupCmd();
    const updaterCmd = farmCreateUpdaterCmd({ relayHost, key });
    const dockerRun = farmDockerRun({ relayHost, key });
    const mdContent = `# Farm setup — ${name}

## 1. Install Docker (Ubuntu / Debian)

Installs Docker and adds the current user to the \`docker\` group so the following
steps can run \`docker\` without sudo.

\`\`\`bash
${installCmd}
\`\`\`

## 2. Create the self-update helper

Installs the host-side updater (\`~/.mobile-farm/mf-update.sh\`) that the panel's
**Restart with update** uses to recreate the container against the host Docker daemon.

\`\`\`bash
${updaterCmd}
\`\`\`

## 3. Stop and remove the existing container and image

For a manual update of an already-running farm, start from this step (3), then
re-run step 4 to pull and launch the latest image (\`docker rmi\` removes the local
image, so step 4's \`docker run\` auto-pulls the latest).

\`\`\`bash
${cleanupCmd}
\`\`\`

## 4. Start the container

\`\`\`bash
${dockerRun}
\`\`\`

> **Note:** ADB keys are stored at \`/var/lib/mobile-farm-adb\`, per-device video
> settings at \`/var/lib/mobile-farm-data\`, scrcpy session recordings in the current
> directory at \`$(pwd)/mobile-farm-recordings\` (one sub-folder per device), clicker logs at
> \`/var/lib/mobile-farm-logs\`, and the duo deposit cache (processed-deposit
> hashes + low-balance/no-ops notify timestamps) at \`/var/lib/mobile-farm-cache\`
> on the host.
> These directories survive container rebuilds and \`docker system prune\`.
> The account-number cache lives inside the container at \`/app/account-cache\`
> (not mounted to any volume), so it is ephemeral and wiped on container recreation.
> The phone only needs to approve USB debugging once per farm machine.

## 5. Initial setup

Open **http://localhost:8000** in a browser on the farm machine.

- Go to **Settings** and verify relay connection is active.
- Connect Android device via USB and confirm USB debugging when prompted on the device screen.
- The device should appear in the device list. If status shows \`unauthorized\`, unlock the device and tap **Allow** on the USB debugging dialog.
`;
    return { installCmd, cleanupCmd, updaterCmd, dockerRun, mdContent };
}
/**
 * Inner HTML for the launch/setup steps (no modal chrome). Shared by the
 * farm-config modal and the farm-settings "Launch / Setup" tab. Copy buttons
 * carry a `data-copy` key wired up by {@link wireLaunchSetup}.
 */
export function launchSetupStepsHtml(s) {
    return `
        <p class="modal__text">${t('farms.launch_step1')}</p>
        <div class="launch-block">
            <pre class="launch-pre">${esc(s.installCmd)}</pre>
            <button class="tbl-btn copy-key-btn" data-copy="install" title="${t('common.copy')}">⎘ ${t('common.copy')}</button>
        </div>
        <p class="modal__text" style="margin-top:12px">${t('farms.launch_step2')}</p>
        <div class="launch-block">
            <pre class="launch-pre">${esc(s.updaterCmd)}</pre>
            <button class="tbl-btn copy-key-btn" data-copy="updater" title="${t('common.copy')}">⎘ ${t('common.copy')}</button>
        </div>
        <p class="modal__text" style="margin-top:12px">${t('farms.launch_step3')}</p>
        <div class="launch-block">
            <pre class="launch-pre">${esc(s.cleanupCmd)}</pre>
            <button class="tbl-btn copy-key-btn" data-copy="cleanup" title="${t('common.copy')}">⎘ ${t('common.copy')}</button>
        </div>
        <p class="modal__text" style="margin-top:12px">${t('farms.launch_step4')}</p>
        <div class="launch-block">
            <pre class="launch-pre">${esc(s.dockerRun)}</pre>
            <button class="tbl-btn copy-key-btn" data-copy="run" title="${t('common.copy')}">⎘ ${t('common.copy')}</button>
        </div>
        <p class="modal__text" style="margin-top:6px;font-size:0.85em;opacity:0.7">
            ${t('farms.launch_paths_note')}
        </p>
        <p class="modal__text" style="margin-top:12px">${t('farms.launch_step5')}</p>
        <ul class="modal__text" style="margin:6px 0 0 18px;line-height:1.7">
            <li>${t('farms.launch_li1')}</li>
            <li>${t('farms.launch_li2')}</li>
            <li>${t('farms.launch_li3')}</li>
        </ul>
        <div class="modal__actions">
            <button type="button" class="tbl-btn" data-launch-download style="margin-right:auto">${t('farms.download_md')}</button>
        </div>
    `;
}
/** Wire copy + download handlers for markup produced by {@link launchSetupStepsHtml}. */
export function wireLaunchSetup(root, s, name) {
    const cmds = {
        install: s.installCmd,
        cleanup: s.cleanupCmd,
        updater: s.updaterCmd,
        run: s.dockerRun,
    };
    root.querySelectorAll('[data-copy]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const cmd = cmds[btn.dataset['copy'] ?? ''];
            if (cmd === undefined)
                return;
            void navigator.clipboard.writeText(cmd);
            btn.textContent = `✓ ${t('common.copied')}`;
            setTimeout(() => { btn.textContent = `⎘ ${t('common.copy')}`; }, 1500);
        });
    });
    root.querySelector('[data-launch-download]')?.addEventListener('click', () => {
        downloadMd(s.mdContent, `farm-setup-${name}`);
    });
}
export function showLaunchConfigModal(name, key) {
    const modal = openModal(`
        <h2 class="modal__title">${t('farms.farm_config_title', { name: esc(name) })}</h2>
        <div id="lc-body"></div>
    `);
    modal.classList.add('modal--wide');
    renderLaunchSetupBody(modal.querySelector('#lc-body'), name, key);
    onModalClose(modal, () => { closeModal(); void renderFarms(); });
}
/**
 * Post-create config for an aggregator farm: there is no docker agent to launch —
 * instead phones running the mobile farm client connect directly with this relay
 * host + farm key. Each phone registers as one device under this farm.
 */
export function showAggregatorConfigModal(name, _key) {
    const modal = openModal(`
        <h2 class="modal__title">${t('farms.aggregator_farm_title', { name: esc(name) })}</h2>
        <p class="modal__text">${t('farms.aggregator_modal_text')}</p>
        ${aggregatorSetupStepsHtml()}
    `);
    modal.classList.add('modal--wide');
    onModalClose(modal, () => { closeModal(); void renderFarms(); });
}
/**
 * Post-create config for a duo farm: no agent to launch — show the DuoPlus setup
 * steps (API key + ADB IP whitelist). The full instructions also live in the farm's
 * "Duo Setup" settings tab.
 */
export function showDuoConfigModal(name) {
    const modal = openModal(`
        <h2 class="modal__title">${t('farms.duo_farm_title', { name: esc(name) })}</h2>
        <p class="modal__text">${t('farms.duo_modal_text')}</p>
        ${duoSetupStepsHtml()}
    `);
    modal.classList.add('modal--wide');
    wireDuoSetup(modal);
    onModalClose(modal, () => { closeModal(); void renderFarms(); });
}
/**
 * Post-create config for a GeeLark farm: no docker agent to launch — it syncs GeeLark
 * cloud phones over the GeeLark Open API. Just confirm the creds are set and point the
 * admin at what to enable in the GeeLark console.
 */
export function showGeelarkConfigModal(name) {
    const modal = openModal(`
        <h2 class="modal__title">${t('farms.geelark_farm_title', { name: esc(name) })}</h2>
        <p class="modal__text">${t('farms.geelark_modal_text')}</p>
        <ul class="modal__text" style="margin:6px 0 0 18px;line-height:1.7">
            <li>${t('farms.geelark_li1')}</li>
            <li>${t('farms.geelark_li2')}</li>
            <li>${t('farms.geelark_li3')}</li>
        </ul>
    `);
    onModalClose(modal, () => { closeModal(); void renderFarms(); });
}
export function showAdspowerConfigModal(name, key) {
    const modal = openModal(`
        <h2 class="modal__title">${t('farms.adspower_farm_title', { name: esc(name) })}</h2>
        <p class="modal__text">${t('farms.adspower_modal_text')}</p>
        <div class="modal__field">
            <label>${t('farms.adspower_key_label')}</label>
            <input id="adspower-key" type="text" readonly value="${esc(key)}" style="font-family:monospace" />
            <button type="button" class="modal__submit" id="adspower-copy" style="margin-top:8px">${t('common.copy')}</button>
        </div>
        <p class="modal__hint">${t('farms.adspower_setup_hint')}</p>
    `);
    modal.querySelector('#adspower-copy').addEventListener('click', () => {
        void navigator.clipboard.writeText(key);
    });
    onModalClose(modal, () => { closeModal(); void renderFarms(); });
}
export function showDeleteFarmConfirm(id, name) {
    const modal = openModal(`
        <h2 class="modal__title">${t('farms.delete_farm')}</h2>
        <p class="modal__text">${t('farms.delete_farm_confirm', { name: esc(name) })}</p>
        <div id="ff-error" class="modal__error" style="display:none"></div>
        <div class="modal__actions">
            <button type="button" class="modal__cancel" id="ff-cancel">${t('common.cancel')}</button>
            <button type="button" class="modal__submit modal__submit--danger" id="ff-confirm">${t('common.delete')}</button>
        </div>
    `);
    modal.querySelector('#ff-cancel').addEventListener('click', closeModal);
    modal.querySelector('#ff-confirm').addEventListener('click', async () => {
        const errorEl = modal.querySelector('#ff-error');
        try {
            const res = await fetch(`/api/farms/${id}`, { method: 'DELETE' });
            const data = await res.json();
            if (!res.ok) {
                showModalErr(errorEl, data.error ?? t('farms.err_delete_failed'));
                return;
            }
            closeModal();
            void renderFarms();
        }
        catch {
            showModalErr(errorEl, t('farms.err_network'));
        }
    });
}
export function showRenameFarmModal(id, currentName) {
    if (!can('farm.rename'))
        return;
    const modal = openModal(`
        <h2 class="modal__title">${t('farms.rename_farm')}</h2>
        <form id="rename-farm-form" class="modal__form" novalidate>
            <div class="modal__field">
                <label>${t('farms.farm_name')}</label>
                <input id="rf-name" type="text" autocomplete="off" value="${esc(currentName)}" maxlength="100" required />
            </div>
            <div id="rf-error" class="modal__error" style="display:none"></div>
            <div class="modal__actions">
                <button type="button" class="modal__cancel" id="rf-cancel">${t('common.cancel')}</button>
                <button type="submit" class="modal__submit">${t('common.save')}</button>
            </div>
        </form>
    `);
    const form = modal.querySelector('#rename-farm-form');
    const input = modal.querySelector('#rf-name');
    modal.querySelector('#rf-cancel').addEventListener('click', closeModal);
    input.focus();
    input.select();
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const name = input.value.trim();
        const errorEl = modal.querySelector('#rf-error');
        const btn = modal.querySelector('.modal__submit');
        errorEl.style.display = 'none';
        if (!name) {
            showModalErr(errorEl, t('farms.err_name_required'));
            return;
        }
        if (name.length > 100) {
            showModalErr(errorEl, t('farms.err_name_too_long'));
            return;
        }
        if (name === currentName) {
            closeModal();
            return;
        }
        btn.disabled = true;
        try {
            const res = await fetch(`/api/farms/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name }),
            });
            const data = await res.json();
            if (!res.ok) {
                showModalErr(errorEl, data.error ?? t('farms.err_rename_failed'));
                return;
            }
            closeModal();
            void renderFarms();
        }
        catch {
            showModalErr(errorEl, t('farms.err_network'));
        }
        finally {
            btn.disabled = false;
        }
    });
}
